// ============================================================
// PREPARED STATES - stances the engine consumes or forgets.
//
// Aim, a won Feint and a Defensive Stance are promises about the NEXT
// thing you do, and the rulebook is specific about how each promise
// dies: aim is spent by the attack it improved, or thrown away the
// moment you react or do anything else; a feint advantage rides exactly
// one melee attack and goes stale at your next turn; a stance holds
// until your next turn starts.
//
// These live as condition codes on CombatantState (synced, undoable,
// visible as chips) but are EVENT-expired - none of them is in
// DURATION_CONDS, so the round countdown never touches them. This
// module is the one place the expiry events are spelled out.
//
// Pure. Every function returns a NEW array plus the labels of what it
// removed, so callers can log the advisory ("Aim lost - reaction
// taken") without re-deriving it.
// ============================================================
import { condType, COND_NAMES } from './conditions';

export interface StateExpiry {
  conds: string[];
  /** Human labels of what was removed, in removal order. */
  removed: string[];
}

function strip(conds: string[], codes: string[]): StateExpiry {
  const removed: string[] = [];
  const kept = conds.filter((c) => {
    const ct = condType(c);
    if (codes.includes(ct)) {
      removed.push(COND_NAMES[ct] ?? ct);
      return false;
    }
    return true;
  });
  return { conds: kept, removed };
}

/**
 * After an attack resolves: aim is spent whatever the weapon; the feint
 * advantage is spent only by the melee attack it promised (a ranged
 * shot neither uses nor loses it - it simply never applied).
 */
export function consumeAfterAttack(conds: string[], opts: { isMelee: boolean }): StateExpiry {
  return strip(conds, opts.isMelee ? ['Ah', 'Af', 'Ft'] : ['Ah', 'Af']);
}

/** RAW: performing a Reaction loses the aim. */
export function dropOnReaction(conds: string[]): StateExpiry {
  return strip(conds, ['Ah', 'Af']);
}

/** RAW: any action other than the attack loses the aim too. */
export function dropOnNonAttackAction(conds: string[]): StateExpiry {
  return strip(conds, ['Ah', 'Af']);
}

/**
 * At the start of the owner's OWN turn: the stances end - Defensive
 * Stance, the Guarded Attack's +10 and the All-Out Attack's forfeited
 * reactions alike, all three worded "until the start of your next
 * turn" - and a feint from last round that never found its attack goes
 * stale. Ao expiring HERE is what makes it survivable: it is the only
 * thing that gives your reactions back. Aim survives
 * - RAW lets an aim taken at the end of one turn feed the first attack
 * of the next, and dropping it here would make Full Aim + attack
 * impossible across the boundary.
 */
export function expireAtTurnStart(conds: string[]): StateExpiry {
  // Unaware (v8.440.0) is not a stance, but it dies the same way: whoever gets a turn knows there is a fight.
  // Ran, Tactical Advance and Disengaged (2026-10-26) are "this turn" states: his next turn starts afresh.
  return strip(conds, ['Ds', 'Gd', 'Ao', 'Ov', 'Ft', 'Unw', 'Rn', 'Ta', 'Dg']);
}

/**
 * When the session PHASE changes (v8.430.0): every promise about "the next
 * thing you do" was made in the scene that just ended. An aim lined up
 * during a conversation is not carried into the fight that follows it, a
 * kill zone declared in a fight does not stand over the infiltration after
 * it. Aim, feint, the stances and Overwatch all go.
 */
export function dropOnPhaseChange(conds: string[]): StateExpiry {
  return strip(conds, ['Ah', 'Af', 'Ft', 'Ds', 'Gd', 'Ao', 'Ov', 'Rn', 'Ta', 'Dg']);
}

/**
 * A MOVE MADE (2026-10-26): moving is not attacking, so a held aim is lost; a Tactical Advance is spent by the
 * move it was for, a Disengage by the move it covers; and a move at Run pace leaves him Ran until his next turn -
 * or, with no turn tracker to end it, until he next moves at any other pace. `removed` names what went, for the log;
 * Ran arriving is not news.
 */
export function afterMove(conds: string[], mode: string, trackerOn: boolean): StateExpiry {
  const ran = mode === 'run';
  // A Disengage covers the one move it is made for (the review: with no tracker it never cleared).
  const spent = strip(conds, ['Ah', 'Af', 'Ta', 'Dg', ...(ran || trackerOn ? [] : ['Rn'])]);
  if (!ran || spent.conds.some((c) => condType(c) === 'Rn')) return spent;
  return { ...spent, conds: [...spent.conds, 'Rn'] };
}
